import { carriedThreads, clientsOnFire, isCarried, lastDigestMoment, splitAtMarker, splitDays, type NotificationDay } from './days'
import { threadNotifications, type NotificationThread } from './threading'
import type { NotificationRow } from '../model/notification-row'
import {
  isSnoozed,
  visibleNotifications,
  type NotificationPreferences,
} from '../model/notification-store'

/**
 * The two surfaces' shared reading of a set of rows, as a pure function.
 *
 * IT TAKES ITS ROWS RATHER THAN READING A STORE, because there are two sets of
 * rows now. The tray's page lives in the store and is what the bell draws and
 * what the verbs patch (plan ruling 17). The centre's SEARCHED page is its own
 * query, because a needle typed in the centre used to narrow the one read both
 * surfaces mount: the store's whole contents became the matches, so the tray
 * tile, the bell panel, the clients on fire, the carried strip and the settled
 * folds all showed the search result under a badge that stayed wide. The needle
 * is a property of the view that asked for it, so the view has its own rows and
 * this function reads whichever it is given.
 */
export interface DerivedNotifications {
  readonly at: Date
  readonly position: Date
  readonly untouched: boolean
  readonly carried: readonly NotificationThread[]
  readonly fresh: readonly NotificationThread[]
  readonly seen: readonly NotificationThread[]
  readonly days: readonly NotificationDay[]
  readonly onFire: ReadonlySet<string>
  readonly snoozed: readonly NotificationThread[]
  readonly done: readonly NotificationThread[]
}

export const deriveNotifications = (
  rows: readonly NotificationRow[],
  preferences: NotificationPreferences,
  lastOpenedAt: string | null,
): DerivedNotifications => {
  const at = new Date()
  const visible = visibleNotifications(rows, preferences, at)
  const position = lastOpenedAt ? new Date(lastOpenedAt) : lastDigestMoment(at, preferences.digest.at)

  const carried = carriedThreads(visible, at)
  const rest = visible.filter((n) => !isCarried(n, at))
  const flat = threadNotifications(rest, at)
    .slice()
    .sort((a, b) => new Date(b.head.createdAt).getTime() - new Date(a.head.createdAt).getTime())
  const { fresh, seen } = splitAtMarker(flat, position)

  return {
    at,
    position,
    untouched: lastOpenedAt === null,
    carried,
    fresh,
    seen,
    days: splitDays(rest, at, preferences.digest.at),
    onFire: clientsOnFire(rows),
    snoozed: threadNotifications(
      rows.filter((n) => isSnoozed(n, at)),
      at,
    ),
    done: threadNotifications(
      rows.filter((n) => n.doneAt !== null),
      at,
    ),
  }
}
